import { defineStore } from "pinia";
import { computed, ref, toValue, type MaybeRefOrGetter } from "vue";
import { useUserStore } from "./user.store";
import { useMutation } from "@vue/apollo-composable";
import { UpdateMeDocument } from "@/generated/graphql";
import { useDumbStorage } from "@/composables/use-dumb-storage";

/**
 * @deprecated No longer used. The realtime translation path has been removed.
 * Accepted for backward compatibility but ignored by useObjectTranslation().
 */
export type ObjectTranslationAccessors = {
  objectId: string | null | undefined;
  objectProperty: string | string[];
};

export const useTranslationStore = defineStore("translation", () => {
  const userStore = useUserStore();
  const showAutoTranslateConfirmation = ref(false);

  const showMobileAutoTranslateConfirmation = ref(false);
  const enableMobileAutoTranslateConfirmation = useDumbStorage(
    "enableMobileAutoTranslateConfirmation",
    true,
    {
      serializer: {
        read: (value) => value === "true",
        write: (value) => (value ? "true" : "false"),
      },
    },
  );

  const autoTranslateOn = computed(
    () => userStore.user?.auto_translate_content ?? true,
  );

  const { mutate: updateMe, loading: updatingAutoTranslate } =
    useMutation(UpdateMeDocument);

  async function toggleAutoTranslate() {
    await updateMe({
      auto_translate_content: !autoTranslateOn.value,
    });
    userStore.updateUser({ auto_translate_content: !autoTranslateOn.value });
  }

  return {
    showMobileAutoTranslateConfirmation,
    enableMobileAutoTranslateConfirmation,
    showAutoTranslateConfirmation,
    autoTranslateOn,
    toggleAutoTranslate,
    updatingAutoTranslate,
  };
});

export function useTranslateObjectSimple() {
  const translationStore = useTranslationStore();

  function translateObject<T extends Record<string, unknown>>({
    objectTranslations,
    object,
    userLanguage,
    localAutoTranslate,
  }: {
    objectTranslations: (T & { language: string })[];
    object: T;
    userLanguage: string;
    localAutoTranslate?: boolean | null;
  }): T {
    const shouldUseTranslation =
      localAutoTranslate ?? translationStore.autoTranslateOn;

    if (shouldUseTranslation) {
      for (const translation of objectTranslations) {
        if (translation.language === userLanguage) {
          return translation;
        }
      }
    }

    return object;
  }

  return { translateObject };
}

export function useObjectTranslation(
  options: MaybeRefOrGetter<{
    authorUserId: string | undefined | null;
    /**
     * @deprecated No longer used. The realtime translation path has been removed.
     * Accepted for backward compatibility but ignored.
     */
    objectAccessors?: ObjectTranslationAccessors;
    originalText: string;
    translatedText: string | undefined | null;
    localAutoTranslate?: boolean | null;
  } | null>,
) {
  const userStore = useUserStore();
  const translationStore = useTranslationStore();

  const translatedText = computed(() => {
    const optionsValue = toValue(options);
    return optionsValue?.translatedText ?? null;
  });

  const hasTranslation = computed(() => translatedText.value !== null);
  const isAuthor = computed(() => {
    const optionsValue = toValue(options);
    if (!optionsValue || !userStore.userId) return false;

    return userStore.userId === optionsValue.authorUserId;
  });

  const canShowTranslateButton = computed(() => {
    return !isAuthor.value && hasTranslation.value;
  });

  const shouldHideContent = computed(() => {
    const optionsValue = toValue(options);
    // If the content is not auto-translated, we should hide it
    const autoTranslate =
      optionsValue?.localAutoTranslate ?? translationStore.autoTranslateOn;

    if (autoTranslate === false) {
      return false;
    }

    if (userStore.userId === optionsValue?.authorUserId) {
      return false;
    }

    return !hasTranslation.value;
  });

  const isTranslated = computed(() => {
    const optionsValue = toValue(options);
    if (!optionsValue) return false;
    if (isAuthor.value) return false;

    return optionsValue?.localAutoTranslate ?? translationStore.autoTranslateOn;
  });

  const text = computed(() => {
    const optionsValue = toValue(options);
    if (!optionsValue) return null;

    const originalText = optionsValue.originalText;

    if (!isTranslated.value) return originalText;

    return translatedText.value ?? originalText;
  });

  return {
    translatedText,
    hasTranslation,
    shouldHideContent,
    canShowTranslateButton,
    isTranslated,
    text,
  };
}

export function useLocalAutoTranslate() {
  return ref<boolean | null>(null);
}
